import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabase';
import { format, subDays, isWithinInterval, startOfDay } from 'date-fns';
import { 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  Area, AreaChart 
} from 'recharts';
import './ProductAnalytics.css';

interface ProductPerformance {
  id: string;
  title: string;
  view_count: number;
  sales_count: number;
  conversion_rate: number;
  revenue: number;
  price: number;
  type: 'product' | 'membership';
}

interface ProductAnalyticsProps {
  initialProductId?: string | null;
}

const ProductAnalytics: React.FC<ProductAnalyticsProps> = ({ initialProductId = null }) => {
  const [products, setProducts] = useState<ProductPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'views' | 'sales' | 'conversion' | 'revenue'>('views');
  const [selectedProductId, setSelectedProductId] = useState<string | null>(initialProductId);
  const [productChartRange, setProductChartRange] = useState<'90d' | 'all'>('90d');
  const [productRevenueData, setProductRevenueData] = useState<Array<{ name: string; revenue: number; views: number; date: Date; sales: number }>>([]);
  const [productRevenueDataAll, setProductRevenueDataAll] = useState<Array<{ name: string; revenue: number; views: number; date: Date; sales: number }>>([]);
  const [activeMetric, setActiveMetric] = useState<'views' | 'sales' | 'revenue' | 'conversion' | 'all'>('all');

  useEffect(() => {
    fetchProductAnalytics();
  }, []);

  useEffect(() => {
    if (initialProductId) {
      setSelectedProductId(initialProductId);
    }
  }, [initialProductId]);

  useEffect(() => {
    const buildProductSeries = async () => {
      if (!selectedProductId) {
        setProductRevenueData([]);
        setProductRevenueDataAll([]);
        return;
      }

      const now = new Date();
      const startDate = subDays(startOfDay(now), 89);

      // Fetch from both tables for the selected item
      const [purchasesRes, membershipsRes, viewsRes] = await Promise.all([
        supabase
          .from('digitalproducts_purchases')
          .select('price, created_at')
          .eq('digitalproducts_id', selectedProductId)
          .eq('payment_status', 'completed')
          .gte('created_at', startDate.toISOString()),
        supabase
          .from('membership')
          .select('price, start_date, status')
          .eq('digitalproducts_id', selectedProductId)
          .gte('start_date', startDate.toISOString()),
        supabase
          .from('product_views')
          .select('viewed_at')
          .eq('product_id', selectedProductId)
          .gte('viewed_at', startDate.toISOString())
      ]);

      const recentPurchases = [
        ...(purchasesRes.data || []),
        ...(membershipsRes.data || [])
          .filter(r => r.status === 'active' || r.status === 'completed' || r.status === 'paid')
          .map(r => ({ price: r.price, created_at: r.start_date }))
      ];
      const recentViews = viewsRes.data || [];

      const days: { date: Date; key: string }[] = Array.from({ length: 90 }, (_, i) => {
        const d = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
        return { date: d, key: format(d, 'MM-dd') };
      });

      const dayMap: Record<string, { revenue: number; views: number; sales: number }> = {};
      days.forEach(d => { dayMap[d.key] = { revenue: 0, views: 0, sales: 0 }; });
      
      recentPurchases.forEach(r => {
        const k = format(new Date(r.created_at), 'MM-dd');
        if (dayMap[k] !== undefined) {
          dayMap[k].revenue += r.price || 0;
          dayMap[k].sales += 1;
        }
      });
      recentViews.forEach(v => {
        const k = format(new Date(v.viewed_at), 'MM-dd');
        if (dayMap[k] !== undefined) dayMap[k].views += 1;
      });

      const series = days.map((d) => {
        const data = dayMap[d.key];
        return {
          name: d.key,
          date: d.date,
          revenue: Number(data.revenue.toFixed(2)),
          views: data.views,
          sales: data.sales
        };
      });
      setProductRevenueData(series);

      // Fetch all time
      const [allPurchasesRes, allMembershipsRes, allViewsRes] = await Promise.all([
        supabase
          .from('digitalproducts_purchases')
          .select('price, created_at')
          .eq('digitalproducts_id', selectedProductId)
          .eq('payment_status', 'completed'),
        supabase
          .from('membership')
          .select('price, start_date, status')
          .eq('digitalproducts_id', selectedProductId),
        supabase
          .from('product_views')
          .select('viewed_at')
          .eq('product_id', selectedProductId)
      ]);

      const allPurchases = [
        ...(allPurchasesRes.data || []),
        ...(allMembershipsRes.data || [])
          .filter(r => r.status === 'active' || r.status === 'completed' || r.status === 'paid')
          .map(r => ({ price: r.price, created_at: r.start_date }))
      ];
      const allViews = allViewsRes.data || [];

      let earliest: Date | null = null;
      allPurchases.forEach(r => {
        const d = new Date(r.created_at);
        if (!earliest || d < earliest) earliest = d;
      });
      allViews.forEach(v => {
        const d = new Date(v.viewed_at);
        if (!earliest || d < earliest) earliest = d;
      });

      const allStart = earliest ? startOfDay(new Date(earliest)) : startDate;
      const totalDays = Math.max(1, Math.ceil((now.getTime() - allStart.getTime()) / (24 * 60 * 60 * 1000)) + 1);
      
      const allDays: { date: Date; key: string }[] = Array.from({ length: totalDays }, (_, i) => {
        const d = new Date(allStart.getTime() + i * 24 * 60 * 60 * 1000);
        return { date: d, key: format(d, 'yyyy-MM-dd') };
      });

      const allDayMap: Record<string, { revenue: number; views: number; sales: number }> = {};
      allDays.forEach(d => { allDayMap[d.key] = { revenue: 0, views: 0, sales: 0 }; });
      
      allPurchases.forEach(r => {
        const k = format(new Date(r.created_at), 'yyyy-MM-dd');
        if (allDayMap[k] !== undefined) {
          allDayMap[k].revenue += r.price || 0;
          allDayMap[k].sales += 1;
        }
      });
      allViews.forEach(v => {
        const k = format(new Date(v.viewed_at), 'yyyy-MM-dd');
        if (allDayMap[k] !== undefined) allDayMap[k].views += 1;
      });

      const seriesAll = allDays.map((d) => {
        const data = allDayMap[d.key];
        return {
          name: d.key,
          date: d.date,
          revenue: Number(data.revenue.toFixed(2)),
          views: data.views,
          sales: data.sales
        };
      });
      setProductRevenueDataAll(seriesAll);
    };
    buildProductSeries();
  }, [selectedProductId, products]);

  const fetchProductAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('User not authenticated');
        return;
      }

      const { data: productsData, error: productsError } = await supabase
        .from('digitalproducts')
        .select('id, title, view_count, price')
        .eq('creator_id', user.id)
        .eq('is_deleted', false);

      if (productsError) throw productsError;

      const { data: membershipsData } = await supabase
        .from('creator_memberships')
        .select('id, name, price')
        .eq('creator_id', user.id);

      const { data: membershipSalesData } = await supabase
        .from('membership')
        .select('digitalproducts_id, title, price, status')
        .eq('creator_id', user.id);
      
      const membershipSales = (membershipSalesData || []).filter(s => 
        s.status === 'active' || s.status === 'completed' || s.status === 'paid'
      );

      const productsWithAnalytics = await Promise.all(
        (productsData || []).map(async (product) => {
          const { data: salesData } = await supabase
            .from('digitalproducts_purchases')
            .select('price')
            .eq('digitalproducts_id', product.id)
            .eq('payment_status', 'completed');

          const sales = salesData || [];
          const relevantMembershipSales = membershipSales.filter(s => s.digitalproducts_id === product.id);
          const salesCount = sales.length + relevantMembershipSales.length;
          const totalRevenue = sales.reduce((sum, sale) => sum + (sale.price || 0), 0) + 
                               relevantMembershipSales.reduce((sum, sale) => sum + (sale.price || 0), 0);
          
          const views = product.view_count || 0;
          const conversionRate = views > 0 ? (salesCount / views) * 100 : 0;

          return {
            id: product.id,
            title: product.title,
            view_count: views,
            sales_count: salesCount,
            conversion_rate: parseFloat(conversionRate.toFixed(2)),
            revenue: parseFloat(totalRevenue.toFixed(2)),
            price: product.price || 0,
            type: 'product' as const
          };
        })
      );

      const membershipsWithAnalytics = (membershipsData || []).map((m) => {
        const relevantSales = membershipSales.filter(s => s.title === m.name);
        const salesCount = relevantSales.length;
        const totalRevenue = relevantSales.reduce((sum, s) => sum + (s.price || 0), 0);
        
        return {
          id: m.id,
          title: m.name,
          view_count: 0, 
          sales_count: salesCount,
          conversion_rate: 0,
          revenue: parseFloat(totalRevenue.toFixed(2)),
          price: m.price || 0,
          type: 'membership' as const
        };
      });

      const combinedAnalytics = [...productsWithAnalytics, ...membershipsWithAnalytics];
      setProducts(combinedAnalytics);
      if (!selectedProductId) {
        setSelectedProductId(combinedAnalytics[0]?.id || null);
      }
    } catch (err) {
      console.error('Error fetching product analytics:', err);
      setError('Failed to load product analytics data');
    } finally {
      setLoading(false);
    }
  };

  const trends = useMemo(() => {
    if (!productRevenueData.length) return { views: 0, sales: 0, revenue: 0 };
    
    const now = new Date();
    const last7DaysStart = subDays(startOfDay(now), 7);
    const prev7DaysStart = subDays(startOfDay(now), 14);

    const calculateTotal = (start: Date, end: Date, key: 'views' | 'sales' | 'revenue') => {
      return productRevenueData
        .filter(d => isWithinInterval(d.date, { start, end }))
        .reduce((sum, d) => sum + d[key], 0);
    };

    const last7Views = calculateTotal(last7DaysStart, now, 'views');
    const prev7Views = calculateTotal(prev7DaysStart, last7DaysStart, 'views');
    
    const last7Sales = calculateTotal(last7DaysStart, now, 'sales');
    const prev7Sales = calculateTotal(prev7DaysStart, last7DaysStart, 'sales');
    
    const last7Rev = calculateTotal(last7DaysStart, now, 'revenue');
    const prev7Rev = calculateTotal(prev7DaysStart, last7DaysStart, 'revenue');

    const getTrend = (current: number, previous: number) => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return ((current - previous) / previous) * 100;
    };

    return {
      views: getTrend(last7Views, prev7Views),
      sales: getTrend(last7Sales, prev7Sales),
      revenue: getTrend(last7Rev, prev7Rev)
    };
  }, [productRevenueData]);

  const sortedProducts = [...products].sort((a, b) => {
    switch (sortBy) {
      case 'views': return b.view_count - a.view_count;
      case 'sales': return b.sales_count - a.sales_count;
      case 'conversion': return b.conversion_rate - a.conversion_rate;
      case 'revenue': return b.revenue - a.revenue;
      default: return 0;
    }
  });

  const selectedProduct = products.find(p => p.id === selectedProductId) || null;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-date">{label}</p>
          {payload.map((item: any, index: number) => (
            <div key={index} className="tooltip-item">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span className="tooltip-dot" style={{ backgroundColor: item.stroke }}></span>
                <span className="tooltip-label">
                  {item.name === 'revenue' ? 'Revenue' : item.name === 'views' ? 'Visitors' : 'Sales'}
                </span>
              </div>
              <span className="tooltip-value">
                {item.name === 'revenue' ? `$${item.value.toFixed(2)}` : item.value}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const currentChartData = productChartRange === 'all' ? productRevenueDataAll : productRevenueData;

  return (
    <div style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div className="metric-title hide-mobile" style={{ marginBottom: 0, fontSize: '0.9rem' }}>Select Item</div>
          <select
            className="time-select mobile-full-width"
            value={selectedProductId || ''}
            onChange={(e) => setSelectedProductId(e.target.value)}
            style={{ minWidth: '200px', width: '100%', maxWidth: '420px', fontSize: '0.85rem' }}
          >
            {products.map(p => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </select>
        </div>
        {selectedProduct && (
          <div className="metrics-grid" style={{ gap: '0.5rem' }}>
            <div 
              className={`metric-card ${activeMetric === 'views' ? 'active-metric-card' : ''}`} 
              onClick={() => setActiveMetric(activeMetric === 'views' ? 'all' : 'views')}
              style={{ cursor: 'pointer' }}
            >
              <div className="metric-title">Views</div>
              <div className="metric-value">{selectedProduct.view_count.toLocaleString()}</div>
            </div>
            <div 
              className={`metric-card ${activeMetric === 'sales' ? 'active-metric-card' : ''}`} 
              onClick={() => setActiveMetric(activeMetric === 'sales' ? 'all' : 'sales')}
              style={{ cursor: 'pointer' }}
            >
              <div className="metric-title">Sales</div>
              <div className="metric-value">{selectedProduct.sales_count.toLocaleString()}</div>
            </div>
            <div 
              className={`metric-card ${activeMetric === 'revenue' ? 'active-metric-card' : ''}`} 
              onClick={() => setActiveMetric(activeMetric === 'revenue' ? 'all' : 'revenue')}
              style={{ cursor: 'pointer' }}
            >
              <div className="metric-title">Revenue</div>
              <div className="metric-value">${selectedProduct.revenue.toFixed(2)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-title">Price</div>
              <div className="metric-value">${selectedProduct.price.toFixed(2)}</div>
            </div>
            <div 
              className="metric-card" 
              style={{ cursor: 'default' }}
            >
              <div className="metric-title">Conversion</div>
              <div className="metric-value" style={{ 
                WebkitTextFillColor: 'unset',
                background: 'none',
                color: selectedProduct.conversion_rate > 5 ? '#10b981' : selectedProduct.conversion_rate > 2 ? '#f59e0b' : '#ef4444'
              }}>
                {selectedProduct.conversion_rate}%
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="chart-container no-border-mobile" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h3 className="chart-title hide-mobile" style={{ margin: 0 }}>Performance</h3>
          <select
            className="time-select mobile-select"
            value={productChartRange}
            onChange={(e) => setProductChartRange(e.target.value as '90d' | 'all')}
            style={{ maxWidth: 140, marginBottom: 0, padding: '4px 24px 4px 12px', fontSize: '0.8rem' }}
          >
            <option value="90d">90 Days</option>
            <option value="all">All Time</option>
          </select>
        </div>
        <ResponsiveContainer width="100%" height={window.innerWidth < 768 ? 250 : 350}>
          <AreaChart data={currentChartData} margin={{ top: 10, right: 0, left: window.innerWidth < 768 ? -35 : -20, bottom: 0 }}>
            <defs>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.2}/>
                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
              </linearGradient>
            </defs>
            {/* Dashed horizontal gridlines only, like the reference chart */}
            <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="rgba(255,255,255,0.08)" />
            <XAxis 
              dataKey="name" 
              stroke="#555" 
              fontSize={10}
              tickLine={false}
              axisLine={false}
              dy={10}
              interval={Math.floor(currentChartData.length / 5)}
            />
            <YAxis 
              yAxisId="left"
              stroke="#555" 
              fontSize={10}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => {
                if (v === 0) return '0';
                const prefix = activeMetric === 'revenue' ? '$' : '';
                if (v >= 1000) {
                  const k = v / 1000;
                  return `${prefix}${Number.isInteger(k) ? k : k.toFixed(1)}K`;
                }
                return `${prefix}${v}`;
              }}
            />
            {activeMetric === 'all' && (
              <YAxis 
                yAxisId="right"
                orientation="right"
                stroke="#555" 
                fontSize={10}
                tickLine={false}
                axisLine={false}
              />
            )}
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ stroke: 'rgba(255,255,255,0.2)', strokeWidth: 1.5 }}
            />
            {activeMetric === 'revenue' || activeMetric === 'all' ? (
              <Area 
                yAxisId="left"
                type="linear" 
                dataKey="revenue" 
                stroke="#3b82f6" 
                strokeWidth={2}
                fillOpacity={1} 
                fill="url(#colorRevenue)" 
                name="revenue"
                dot={{ r: 3, strokeWidth: 0, fill: '#3b82f6' }}
                activeDot={{ r: 4, stroke: '#fff', strokeWidth: 2, fill: '#3b82f6' }}
              />
            ) : null}
            {activeMetric === 'views' || activeMetric === 'all' ? (
              <Area 
                yAxisId={activeMetric === 'all' ? "right" : "left"}
                type="linear" 
                dataKey="views" 
                stroke="#8b5cf6" 
                strokeWidth={2}
                fillOpacity={1} 
                fill="url(#colorViews)" 
                name="views"
                dot={{ r: 3, strokeWidth: 0, fill: '#8b5cf6' }}
                activeDot={{ r: 4, stroke: '#fff', strokeWidth: 2, fill: '#8b5cf6' }}
              />
            ) : null}
            {activeMetric === 'sales' || activeMetric === 'all' ? (
              <Area 
                yAxisId={activeMetric === 'all' ? "right" : "left"}
                type="linear" 
                dataKey="sales" 
                stroke="#10b981" 
                strokeWidth={2}
                fillOpacity={1} 
                fill="url(#colorSales)" 
                name="sales"
                dot={{ r: 3, strokeWidth: 0, fill: '#10b981' }}
                activeDot={{ r: 4, stroke: '#fff', strokeWidth: 2, fill: '#10b981' }}
              />
            ) : null}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Sort Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <div className="metric-title hide-mobile" style={{ marginBottom: 0, fontSize: '0.85rem' }}>Sort by</div>
        <select 
          className="time-select mobile-select"
          value={sortBy} 
          onChange={(e) => setSortBy(e.target.value as any)}
          style={{ width: '160px', padding: '4px 24px 4px 12px', fontSize: '0.8rem', marginBottom: 0 }}
        >
          <option value="views">Views</option>
          <option value="sales">Sales</option>
          <option value="conversion">Conv. Rate</option>
          <option value="revenue">Revenue</option>
        </select>
      </div>

      {/* Product Analytics Table */}
      <div className="transactions-table no-border-mobile">
        <table>
          <thead>
            <tr>
              <th>Item</th>
              <th className="hide-mobile">Type</th>
              <th>Views</th>
              <th>Sales</th>
              <th>Conv.</th>
              <th className="hide-mobile">Price</th>
              <th>Revenue</th>
            </tr>
          </thead>
          <tbody>
            {sortedProducts.length > 0 ? (
              sortedProducts.map((product) => (
                <tr key={product.id}>
                  <td title={product.title}>
                    {product.title.length > 15 ? product.title.substring(0, 15) + '...' : product.title}
                  </td>
                  <td className="hide-mobile">
                    <span style={{ 
                      padding: '2px 6px', 
                      borderRadius: '4px', 
                      fontSize: '0.65rem',
                      background: product.type === 'membership' ? 'rgba(139, 92, 246, 0.1)' : 'rgba(34, 197, 94, 0.1)',
                      color: product.type === 'membership' ? '#a78bfa' : '#4ade80',
                      border: `1px solid ${product.type === 'membership' ? 'rgba(139, 92, 246, 0.2)' : 'rgba(34, 197, 94, 0.2)'}`
                    }}>
                      {product.type === 'membership' ? 'M' : 'P'}
                    </span>
                  </td>
                  <td>{product.view_count.toLocaleString()}</td>
                  <td>{product.sales_count.toLocaleString()}</td>
                  <td style={{ color: product.conversion_rate > 5 ? '#10b981' : product.conversion_rate > 2 ? '#f59e0b' : '#ef4444', fontWeight: 600 }}>
                    {product.type === 'membership' ? '-' : `${product.conversion_rate}%`}
                  </td>
                  <td className="hide-mobile">${product.price.toFixed(2)}</td>
                  <td>${product.revenue.toFixed(2)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', color: '#444', fontStyle: 'italic', padding: '2rem' }}>
                  No data
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductAnalytics;
